import type { ClientImportBatch } from '@/shared/api'

/**
 * Применение сорвалось.
 *
 * ⚠️ ЭТО ЕДИНСТВЕННОЕ МЕСТО, ГДЕ ЖИВЁТ ПРАВИЛО, и правило шире статуса: непустой `error` означает
 * сбой САМ ПО СЕБЕ, чем бы ни был `status`. Сорвавшееся применение сервис сохраняет сам — ставит
 * `failed` и кладёт причину рядом, — так что вторая половина правила это пояс: `error` заполняет и
 * гейтвей, ради чего поле у него и просили.
 *
 * ⚠️ ЧЕГО ПРАВИЛО НЕ ЛОВИТ, и это их же оговорка: падение самого процесса посреди применения
 * записать статус НЕКОМУ. Такая пачка остаётся в `committing` с ПУСТЫМ `error`, и на вопрос «идёт
 * ли ещё» здесь навсегда отвечается «да».
 *
 * ⚠️ Читать это обязаны ВСЕ, кто спрашивает «идёт ли ещё»: и опрос, и подпись состояния, и
 * заголовок хода, и обещание «можно уйти, запись продолжается». Научить один только опрос мало —
 * экран покажет красную плашку «не применилась» ровно над жёлтым «Загружаем».
 */
export function clientImportFailed(batch: ClientImportBatch): boolean {
  return batch.status === 'failed' || (batch.error ?? '') !== ''
}

/** Судьба загрузки решена — применять или отменять больше нечего (сервер ответит 409). */
export function clientImportSettled(batch: ClientImportBatch): boolean {
  return batch.status === 'committed' || batch.status === 'cancelled' || clientImportFailed(batch)
}

export type ClientImportProgress = { done: number; total: number }

/**
 * Сколько строк применение уже прошло и сколько ему всего предстоит.
 *
 * ⚠️ ТРИ МЕСТА, ГДЕ ЛЕГКО ПОЛУЧИТЬ ЧИСЛО НЕ ПРО ТО:
 *
 * 1. **Знаменатель — `rowsImportable`, а не `totalRows`.** Применение идёт только по строкам,
 *    прошедшим отбор; отсеянные не будут обработаны никогда, и полоса, считающая их, застряла бы
 *    на 90% навсегда — ровно на том экране, где менеджер ждёт окончания.
 * 2. **`rowsFailed` — тоже пройденная строка.** Она не записалась, но применение с ней закончило и
 *    дальше не вернётся. Не считать её значит недосчитывать прогресс тем сильнее, чем хуже файл.
 * 3. **Счётчиков записи может не быть и ПОСЛЕ старта применения.** Сервер обновляет их раз в 200
 *    строк, то есть первые ответы с `committing` приходят вовсе без них, — и это ноль, а не пропуск.
 *    (До применения их не бывает тем более, но туда мы не доходим: ранний выход выше.)
 *
 * Возвращаем `null`, пока применение не началось: полоса прогресса у неприменённого отчёта
 * показывала бы ноль из тысяч, то есть сообщала бы о работе, которой ещё не поручали.
 */
export function clientImportProgress(batch: ClientImportBatch): ClientImportProgress | null {
  // Сорвавшееся применение прогресс тоже имеет — и он там САМОЕ ВАЖНОЕ: запись не откатывается, и
  // «дошло до 4 812 из 13 238» отвечает на единственный вопрос, который у менеджера остаётся.
  if (batch.status !== 'committing' && batch.status !== 'committed' && batch.status !== 'failed') {
    return null
  }

  const done = (batch.rowsCreated ?? 0) + (batch.rowsMatched ?? 0) + (batch.rowsFailed ?? 0)
  // Зажимаем сверху: расхождение счётчиков сервера — это его беда, а полоса, уехавшая за 100%,
  // становится нашей. Само расхождение при этом видно в числах рядом, они не зажаты.
  return { done: Math.min(done, batch.rowsImportable), total: batch.rowsImportable }
}
